import cuid from "cuid";
import { find, get } from "lodash";

/**
 * Build the cartesian set of (base, complement) combinations for the
 * Mobilité Mutuelle multi-combinations feature.
 *
 * Inputs:
 * - bases: array of items shaped like generalCosts entries
 *   `{ insurancePlan, cost, currency, costDetails, ... }` representing
 *   the user-selected Socles.
 * - upsellsByBase: map `{ [baseInsurancePlanId]: [{ insurancePlan, cost,
 *   currency, ... }, ...] }` of selected complementary modules per base.
 *
 * For each base we yield one row "base alone" plus one row per
 * selected complement, so a base with 2 modules a, b yields 3 rows:
 * `base`, `base + a`, `base + b`.
 *
 * Returns an array of combinations:
 * `{ id, baseInsurancePlan, complementaryInsurancePlan | null,
 *    baseCost, complementCost, totalCost, currency, label }`.
 */
export const buildCombinations = ({ bases = [], upsellsByBase = {} }) => {
  const combinations = [];
  bases.forEach((base) => {
    const baseInsurancePlan = get(base, "insurancePlan");
    if (!baseInsurancePlan) return;
    const baseCost = get(base, "cost", 0);
    const currency = get(base, "currency");
    const baseModules = get(upsellsByBase, baseInsurancePlan.id, []);

    combinations.push({
      id: cuid(),
      baseInsurancePlan,
      complementaryInsurancePlan: null,
      baseCost,
      complementCost: 0,
      totalCost: baseCost,
      currency,
      label: get(baseInsurancePlan, "name"),
    });

    baseModules.forEach((module) => {
      const complementaryInsurancePlan = get(module, "insurancePlan");
      if (!complementaryInsurancePlan) return;
      const complementCost = get(module, "cost", 0);
      combinations.push({
        id: cuid(),
        baseInsurancePlan,
        complementaryInsurancePlan,
        baseCost,
        complementCost,
        totalCost: baseCost + complementCost,
        currency: currency || get(module, "currency"),
        label: `${get(baseInsurancePlan, "name")} avec ${get(
          complementaryInsurancePlan,
          "name",
        )}`,
      });
    });
  });
  return combinations;
};

/**
 * Strip the helper-side computed fields and keep only the persistable
 * shape consumed by the API and the SELECT_COMBINATION step:
 * `{ id, baseInsurancePlanId, complementaryInsurancePlanId | null }`.
 */
export const toPersistedCombinations = (combinations) =>
  combinations.map((combination) => ({
    id: combination.id,
    baseInsurancePlanId: get(combination, "baseInsurancePlan.id"),
    complementaryInsurancePlanId:
      get(combination, "complementaryInsurancePlan.id") || null,
  }));

/**
 * Returns the upsell modules that belong to a Mobilité Mutuelle combination.
 * When `combinationId` is set and `complementaryInsurancePlanId` is null, the
 * combination is "base alone" (no complements). When `combinationId` is absent,
 * returns all modules for the base (legacy behaviour).
 */
export const filterUpsellsForCombination = ({
  upsellsByBase = {},
  baseInsurancePlanId,
  combinationId,
  complementaryInsurancePlanId,
}) => {
  const modules = get(upsellsByBase, baseInsurancePlanId, []);
  if (!combinationId) {
    return modules;
  }
  if (!complementaryInsurancePlanId) {
    return [];
  }
  return modules.filter(
    (module) =>
      get(module, "insurancePlan.id") === complementaryInsurancePlanId,
  );
};

/**
 * Resolves the active Mobilité Mutuelle combination for a project subscription.
 * Prefers `fields.selectedCombinationId`, then subscription `additionalInfo`.
 */
export const resolveActiveCombination = ({ project, subscriptionId }) => {
  const combinations = get(project, "fields.combinations", []);
  const currentSubscription = find(get(project, "subscriptions", []), {
    id: subscriptionId,
  });
  const combinationId =
    get(project, "fields.selectedCombinationId") ||
    get(currentSubscription, "additionalInfo.combinationId");
  const combination = find(combinations, { id: combinationId });
  const complementaryInsurancePlanId =
    get(combination, "complementaryInsurancePlanId") ??
    get(currentSubscription, "additionalInfo.complementaryInsurancePlanId");

  return {
    combinationId,
    complementaryInsurancePlanId,
    combination,
    combinations,
    currentSubscription,
  };
};

/**
 * Applies combination metadata from a shared link onto project display data.
 */
export const applyCombinationFromLink = ({
  project,
  linkCombinationId,
  linkComplementaryId,
  linkSubscriptionId,
  currentSubscriptionId,
}) => {
  if (!linkCombinationId || !project) {
    return project;
  }

  const subscriptionId = linkSubscriptionId || currentSubscriptionId;

  return {
    ...project,
    fields: {
      ...get(project, "fields", {}),
      selectedCombinationId: linkCombinationId,
      currentSubscription: subscriptionId,
    },
    subscriptions: (get(project, "subscriptions") || []).map((subscription) =>
      subscription.id === subscriptionId
        ? {
            ...subscription,
            additionalInfo: {
              ...get(subscription, "additionalInfo", {}),
              combinationId: linkCombinationId,
              complementaryInsurancePlanId: linkComplementaryId ?? null,
            },
          }
        : subscription,
    ),
  };
};
